import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { SAVES_TYPES } from 'right-revenue-microservice-sdk/lib/constants';
import session from 'containers/App/session';

import * as actions from '../../actions';
import { compose } from 'redux';

import { Button } from 'components/forms/Button';
import Proposals from './Proposals';
import ADRTable from './ADRTable';
import SummaryTable from './SummaryTable';
import AdditionalSpend from './AdditionalSpend';
import ForecastTable from './ForecastTable';
import Graphs from './Graphs';
import NormalSpend from './NormalSpend';
import FormActions from '../common/FormActions';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../../messages';
import injectSaga from '../../../../utils/redux/injectSaga';
import injectReducer from '../../../../utils/redux/injectReducer';
import { DOMAIN } from '../../constants';
import reducer from '../../reducer';
import saga from '../../saga';

import 'react-toggle/style.css';
import './groupDisplacement.style.css';
import getSymbolFromCurrency from 'currency-symbol-map';
import { getCSVData } from './utils.js';

const Container = styled.div`
    padding: 2em;
`;

const Panel = styled.div`
    padding: 1.5em;
    margin-bottom: 1em
    background-color: white;
    border: 0.1em solid #eaeaea;
    box-shadow: 0 0.2em 0.4em 0 rgba(215, 215, 215, 0.5);
`;

const Title = styled.h2`
    font-family: ${({ theme }) => theme.font.title};
    font-size: 1.5625rem;
    color: ${({ theme }) => theme.colour.dark};
    text-align: left;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
`;

const Title2 = styled.h2`
    font-family: ${({ theme }) => theme.font.title};
    font-size: 1.9em;
    color: ${({ theme }) => theme.colour.dark};
    text-align: left;
    margin-top: 1em;
    margin-bottom: 1em;
`;

const GroupDisplacementForm = () => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const calculateMinRate = () => dispatch(actions.calculateMinRate());
    const resetForecasts = () => dispatch(actions.resetForecasts());

    const removeItem = (fieldId) => {
        dispatch(actions.updateAdditionalSpendFormValue(fieldId, 'DELETE'));
    };

    const onInputChange = (fieldName, value) => {
        dispatch(actions.updateGroupDisplacementProposals(fieldName, value));
        dispatch(actions.updateGroupDisplacementAllForecasts());
        dispatch(actions.updateAdditionalSpendSection(true));
    };

    const onAdditionalSpendInputAdd = () => {
        dispatch(actions.updateAdditionalSpendFormValue(uuidv4(), 'CREATE'));
    };

    const onAdditionalSpendNameChange = (fieldId, value) => {
        dispatch(actions.updateAdditionalSpendFormValue(fieldId, 'NAME', value));
    };

    const onAdditionalSpendInputChange = (fieldId, value) => {
        dispatch(actions.updateAdditionalSpendFormValue(fieldId, 'VALUE', value));
    };

    const onAdditionalSpendSelectChange = (fieldId, value) => {
        dispatch(actions.updateAdditionalSpendFormValue(fieldId, 'PERCENTAGE', value));
    };

    const onNormalSpendInputChange = (_fieldId, value) => {
        dispatch(actions.updateNormalSpendFormValue('VALUE', value));
    };

    const onNormalSpendSelectChange = (_fieldId, value) => {
        dispatch(actions.updateNormalSpendFormValue('PERCENTAGE', value));
    };

    const onDateRangeChange = (dates) => {
        dispatch(actions.updateGroupDisplacementProposals('startDate', dates[0].format('YYYY-MM-DD')));
        dispatch(actions.updateGroupDisplacementProposals('endDate', dates[1].format('YYYY-MM-DD')));
        resetForecasts();
    };

    const onRoomNumberChange = (event) => {
        const { value } = event;
        dispatch(actions.updateGroupDisplacementRoomNumber(value));
    };

    const onADRRowChange = (data) => {
        dispatch(actions.updateGroupDisplacementADRData(data));
        dispatch(actions.updateGroupDisplacementAllForecasts());
    };

    const onForecastRowChange = (data, roomType) => {
        dispatch(actions.updateGroupDisplacementForecastData(data, roomType));
        dispatch(actions.updateAdditionalSpendSection(true));
    };

    const onAddSpendToggleWashChange = (fieldId, value) => {
        dispatch(actions.updateAdditionalSpendFormValue(fieldId, 'TOGGLEWASH', value));
    };

    const onAddSpendToggleCommissionChange = (fieldId, value) => {
        dispatch(actions.updateAdditionalSpendFormValue(fieldId, 'TOGGLECOMMISSION', value));
    };

    const onToggleChange = (event) => {
        const { name, checked } = event.target;
        dispatch(actions.updateGroupDisplacementProposals(name, checked));
        dispatch(actions.updateGroupDisplacementAllForecasts());
        dispatch(actions.updateAdditionalSpendSection(true));
    };

    const currency = useSelector(session.selectors.selectCurrency);

    const formData = useSelector((state) => state.formData.groupDisplacement);

    const csvData = getCSVData(formData);
    const currencyIcon = getSymbolFromCurrency(currency);
    const labelStyle = { width: '25%', paddingLeft: '1em' };
    const selectStyles = {
        option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? 'white' : 'black',
            backgroundColor: state.isSelected ? '#125651' : 'white',
        }),
        control: (provided, state) => ({
            ...provided,
            border: state.isFocused ? '0.1em solid #30b79c' : '0.1em solid #D3D3D3',
            boxShadow: state.isFocused ? '0 0 0.5em rgba(48, 181, 155, 0.6)' : 'none',
            '&:hover': {
                border: '0.1em solid #30B79C',
            },
        }),
    };
    return (
        <Container className="grp-disp-main">
            <Panel>
                <Title>
                    <FormattedMessage {...messages.groupDispTitle} />
                </Title>
            </Panel>
            <Panel style={{ marginBottom: '10em', marginTop: '1em' }}>
                <Proposals
                    formData={formData}
                    currency={currencyIcon}
                    onInputChange={onInputChange}
                    onRoomNumberChange={onRoomNumberChange}
                    onDateRangeChange={onDateRangeChange}
                    onToggleChange={onToggleChange}
                />
                <hr />
                <Title2>
                    <FormattedMessage {...messages.grpStepOneTitle} />
                </Title2>
                <ADRTable formData={formData} onADRRowChange={onADRRowChange} />
                <NormalSpend
                    formData={formData}
                    onInputChange={onNormalSpendInputChange}
                    onSelectChange={onNormalSpendSelectChange}
                    currency={currencyIcon}
                    selectStyles={selectStyles}
                    labelStyle={labelStyle}
                />
                <hr />
                <Title2>
                    <FormattedMessage {...messages.grpStepTwoTitle} />
                </Title2>
                {formData.roomTypes.map((roomType) => (
                    <div key={roomType.id}>
                        <ForecastTable
                            formData={formData}
                            currency={currencyIcon}
                            hotelSize={roomType.hotelSize}
                            onForecastRowChange={(data) => onForecastRowChange(data, roomType.roomType)}
                        />
                    </div>
                ))}
                <hr />
                <Title2>
                    <FormattedMessage {...messages.grpStepThreeTitle} />
                </Title2>
                <AdditionalSpend
                    formData={formData}
                    currency={currencyIcon}
                    selectStyles={selectStyles}
                    removeItem={removeItem}
                    onInputChange={onAdditionalSpendInputChange}
                    onSelectChange={onAdditionalSpendSelectChange}
                    onAddRow={onAdditionalSpendInputAdd}
                    onInputNameChange={onAdditionalSpendNameChange}
                    onToggleWashChange={onAddSpendToggleWashChange}
                    onToggleCommissionChange={onAddSpendToggleCommissionChange}
                />
                <div className="grpDisp-form-submit-row">
                    <Button size="large" type="primary" onClick={calculateMinRate} className="grpDisp-form-submit">
                        <FormattedMessage {...messages.calcMinRateButton} />
                    </Button>
                </div>
                <hr />
                {formData.outcome && (
                    <div className="outcome">
                        <FormattedMessage {...messages.outcomeBreakEven} />{' '}
                        <b>
                            {currencyIcon}
                            {formData.roomTypes[0].minRate}
                        </b>
                        <br />
                        <br />
                        <FormattedMessage {...messages.outcomePartTwo} />{' '}
                        <b>
                            {formData.outcome.substring(0, 5)} {currencyIcon}
                            {formData.outcome.substring(5, 12)}
                        </b>{' '}
                        {intl.formatMessage(messages.outcomePartThree, {
                            value: formData.action,
                        })}
                        <br />
                        <br />
                        <FormattedMessage {...messages.grossRevEstimate} />
                        <br />
                        <FormattedMessage {...messages.acceptingGroupLabel} />{' '}
                        <b>
                            {currencyIcon}
                            {formData.summary.find((element) => element.id === 'withGroup').gross}
                        </b>
                        <br />
                        <FormattedMessage {...messages.rejectingGroupLabel} />{' '}
                        <b>
                            {currencyIcon}
                            {formData.summary.find((element) => element.id === 'withoutGroup').gross}
                        </b>
                        <br />
                        <br />
                        <FormattedMessage {...messages.nettRevEstimate} />
                    </div>
                )}

                <SummaryTable formData={formData.summary} currency={currencyIcon} />

                {formData.summary && <Graphs summary={formData.summary} currency={currencyIcon} />}
            </Panel>
            <FormActions
                formType={SAVES_TYPES.GROUP_DISPLACEMENT}
                extractStateToSave={(state) => ({
                    groupDisplacement: state.groupDisplacement,
                })}
                data={formData}
                exportedCSVData={csvData}
                exportedFileName="right-revenue-group-displacement.csv"
            />
        </Container>
    );
};

const withReducer = injectReducer({ key: DOMAIN, reducer });
const withSaga = injectSaga({ key: DOMAIN, saga });

export default compose(withReducer, withSaga)(GroupDisplacementForm);
